import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Flex, List, Tag, Typography, message } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { STOCK_LOCATIONS_KEY, listStockLocations, makeMainLocation } from '../../components/stock-locations';
import { ApiError } from '../../lib/api-client';

const { Text } = Typography;

/**
 * The branch's stock locations and which is main (Q98). Every picker starts on
 * the main one. Shown only when there is a choice to make; the server refuses
 * the change to anyone without stock adjust, in its own words.
 */
export function StockLocationsCard(): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: locations = [] } = useQuery({ queryKey: STOCK_LOCATIONS_KEY, queryFn: listStockLocations });

  const makeMain = useMutation({
    mutationFn: (uid: string) => makeMainLocation(uid),
    onSuccess: (updated, uid) => {
      queryClient.setQueryData(STOCK_LOCATIONS_KEY, updated);
      const name = updated.find((l) => l.uid === uid)?.name ?? '';
      void message.success(t('stockLocation.madeMain', { name }));
    },
    onError: (e: unknown) => {
      void message.error(e instanceof ApiError ? e.message : t('stockLocation.makeMainFailed'));
    },
  });

  if (locations.length <= 1) return null;

  return (
    <Card title={t('stockLocation.cardTitle')}>
      <Flex vertical gap={8}>
        <Text type="secondary">{t('stockLocation.mainHelp')}</Text>
        <List
          dataSource={locations}
          renderItem={(location) => (
            <List.Item
              key={location.uid}
              actions={
                location.isMain
                  ? [<Tag key="main" color="blue">{t('stockLocation.main')}</Tag>]
                  : [
                      <Button
                        key="make"
                        size="large"
                        loading={makeMain.isPending && makeMain.variables === location.uid}
                        onClick={() => {
                          makeMain.mutate(location.uid);
                        }}
                      >
                        {t('stockLocation.makeMain')}
                      </Button>,
                    ]
              }
            >
              <List.Item.Meta title={location.name} description={location.code} />
            </List.Item>
          )}
        />
      </Flex>
    </Card>
  );
}
